import { CheckCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { StocktakeItemRow, StocktakeRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const TAKE_TYPE_OPTIONS = [
  { label: '全盘', value: 'FULL' },
  { label: '循环盘', value: 'CYCLE' },
  { label: '抽盘', value: 'SPOT' },
  { label: 'RFID', value: 'RFID' }
];

const TAKE_MODE_OPTIONS = [
  { label: '明盘（带出账面数）', value: 'OPEN' },
  { label: '盲盘（隐藏账面数）', value: 'BLIND' }
];

/** 后端只会写 COUNTING（建单）/ DONE（确认）/ CANCELED / CLOSED，不存在 NEW。 */
type StatusFilter = 'ALL' | 'COUNTING' | 'DONE' | 'CANCELED';

const STATUS_META: Record<string, { text: string; color: string }> = {
  COUNTING: { text: '盘点中', color: 'processing' },
  DONE: { text: '已完成', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' },
  CLOSED: { text: '已关闭', color: 'default' }
};

/** 库存盘点：以实盘数调账，差异写 CHECK 流水。 */
export default function StocktakeList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['stocktake', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.stocktake.list({ lastId: pageParam, size: PAGE_SIZE, takeStatus: status === 'ALL' ? undefined : status }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['stocktake', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.stocktake.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const confirm = useMutation({
    mutationFn: () => hq.stocktake.confirm(currentId ?? ''),
    onSuccess: async (result) => {
      message.success(`盘点完成：${result.bizNo}，差异金额 ${formatAmount(result.diffAmount)}`);
      await queryClient.invalidateQueries({ queryKey: ['stocktake'] });
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '确认盘点失败')
  });

  // ---------- 建单 ----------
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<{
    warehouseId: string;
    takeType: string;
    takeMode: string;
    remark?: string;
  }>();

  const warehouses = useQuery({
    queryKey: ['org-structure', 'warehouse', 'options'],
    queryFn: () => hq.orgStructure.warehouse.list(),
    enabled: createOpen,
    staleTime: 5 * 60 * 1000
  });

  const createMutation = useMutation({
    mutationFn: (values: { warehouseId: string; takeType: string; takeMode: string; remark?: string }) =>
      hq.stocktake.create({
        warehouseId: values.warehouseId,
        takeType: values.takeType as 'FULL' | 'CYCLE' | 'SPOT' | 'RFID',
        takeMode: values.takeMode as 'OPEN' | 'BLIND',
        remark: values.remark || undefined
      }),
    onSuccess: async (result) => {
      message.success(`盘点单已生成：${result.bizNo}（${result.itemCount} 行）`);
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['stocktake'] });
      setCurrentId(result.id);
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建单失败')
  });

  // ---------- 实盘录入 ----------
  const [countDrafts, setCountDrafts] = useState<Record<string, number>>({});

  const countMutation = useMutation({
    mutationFn: (payload: { itemId: string; countQty: number }) =>
      hq.stocktake.count(currentId ?? '', payload.itemId, { countQty: payload.countQty }),
    onSuccess: async () => {
      message.success('实盘数已保存');
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<StocktakeRow> = [
    {
      title: '盘点单号',
      dataIndex: 'bizNo',
      width: 220,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '仓库 ID', dataIndex: 'warehouseId', width: 120 },
    { title: '盘点类型', dataIndex: 'takeType', width: 120, render: (value: string | null) => value || '-' },
    { title: '盘点方式', dataIndex: 'takeMode', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '差异金额',
      dataIndex: 'totalDiffAmount',
      width: 140,
      align: 'right',
      render: (value: number) =>
        value === 0 ? formatAmount(0) : (
          <Typography.Text type={value > 0 ? 'danger' : 'success'}>{formatAmount(value)}</Typography.Text>
        )
    },
    {
      title: '状态',
      dataIndex: 'takeStatus',
      width: 110,
      render: (value: string | null) => {
        const meta = STATUS_META[value ?? ''] ?? { text: value ?? '-', color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '业务时间', dataIndex: 'bizDate', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  // 后端 CountAsync 仅拒绝 DONE / CLOSED / CANCELED
  const takeStatus = detail.data?.stocktake.takeStatus ?? '';
  const canCount = takeStatus !== '' && !['DONE', 'CLOSED', 'CANCELED'].includes(takeStatus);
  const isBlind = detail.data?.stocktake.takeMode === 'BLIND';

  const itemColumns: ColumnsType<StocktakeItemRow> = [
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '批次', dataIndex: 'batchNo', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '账面',
      dataIndex: 'bookQty',
      width: 100,
      align: 'right',
      // 盲盘不暴露账面数，避免照抄
      render: (value: number) => (isBlind && canCount ? '—' : formatQty(value))
    },
    {
      title: '实盘',
      dataIndex: 'countQty',
      width: 170,
      align: 'right',
      render: (value: number, row) => {
        if (!canCount) return formatQty(value);
        return (
          <Space.Compact>
            <InputNumber
              min={0}
              step={1}
              style={{ width: 96 }}
              value={countDrafts[row.id] ?? value}
              onChange={(next) =>
                setCountDrafts((prev) => ({ ...prev, [row.id]: Number(next ?? 0) }))
              }
            />
            <Button
              size="small"
              loading={countMutation.isPending}
              onClick={() =>
                countMutation.mutate({ itemId: row.id, countQty: countDrafts[row.id] ?? value })
              }
            >
              保存
            </Button>
          </Space.Compact>
        );
      }
    },
    {
      title: '差异',
      dataIndex: 'diffQty',
      width: 100,
      align: 'right',
      render: (value: number) =>
        value === 0 ? '0' : (
          <Typography.Text type={value > 0 ? 'danger' : 'success'}>
            {value > 0 ? '+' : ''}
            {formatQty(value)}
          </Typography.Text>
        )
    },
    { title: '差异原因', dataIndex: 'diffReason', width: 160, render: (value: string | null) => value || '-' },
    { title: '处理', dataIndex: 'handleType', width: 110, render: (value: string | null) => value || '-' }
  ];

  // CLOSED 同样是终态：漏排会让已关闭的盘点单还显示「确认」按钮，点了必报错
  const canConfirm = detail.data
    ? !['DONE', 'CANCELED', 'CLOSED'].includes(detail.data.stocktake.takeStatus ?? '')
    : false;

  return (
    <Card
      className="page-card"
      title="库存盘点"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.setFieldsValue({ takeType: 'FULL', takeMode: 'OPEN', warehouseId: '' });
              setCreateOpen(true);
            }}
          >
            新建盘点
          </Button>
        </Space>
      }
    >
      <Segmented<StatusFilter>
        value={status}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '盘点中', value: 'COUNTING' },
          { label: '已完成', value: 'DONE' },
          { label: '已取消', value: 'CANCELED' }
        ]}
        onChange={(value) => setStatus(value)}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="盘点单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<StocktakeRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无盘点单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.stocktake.bizNo ?? '盘点单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={880}
        extra={
          canConfirm ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={confirm.isPending}
              onClick={() =>
                modal.confirm({
                  title: '确认盘点',
                  content: '确认后将按实盘数调整库存并写入盘盈/盘亏流水，不可撤销。',
                  okText: '确认盘点',
                  onOk: () => confirm.mutateAsync()
                })
              }
            >
              确认盘点
            </Button>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.stocktake.takeStatus ?? '']?.text ?? detail.data.stocktake.takeStatus}
              </Descriptions.Item>
              <Descriptions.Item label="仓库 ID">{detail.data.stocktake.warehouseId}</Descriptions.Item>
              <Descriptions.Item label="计划开始">{formatDisplayTime(detail.data.stocktake.planBeginAt)}</Descriptions.Item>
              <Descriptions.Item label="计划结束">{formatDisplayTime(detail.data.stocktake.planEndAt)}</Descriptions.Item>
              <Descriptions.Item label="差异金额">{formatAmount(detail.data.stocktake.totalDiffAmount)}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.data.stocktake.remark || '-'}</Descriptions.Item>
            </Descriptions>
            <Table<StocktakeItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="新建盘点单"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
        okText="生成盘点单"
        confirmLoading={createMutation.isPending}
        onOk={async () => {
          const values = await createForm.validateFields();
          createMutation.mutate(values);
        }}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="warehouseId"
            label="盘点仓库"
            rules={[{ required: true, message: '请选择仓库' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={warehouses.isLoading}
              placeholder="选择要盘点的仓库"
              options={(warehouses.data ?? []).map((row) => ({
                label: `${row.whName}（${row.whCode}）`,
                value: row.id
              }))}
            />
          </Form.Item>
          <Form.Item name="takeType" label="盘点类型" rules={[{ required: true }]}>
            <Select options={TAKE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="takeMode" label="盘点方式" rules={[{ required: true }]}>
            <Select options={TAKE_MODE_OPTIONS} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
          <Typography.Text type="secondary">
            建单会按所选仓库的账面库存自动生成明细行，建单后即可录入实盘数。
          </Typography.Text>
        </Form>
      </Modal>
    </Card>
  );
}
